import { Dispatch, FunctionComponent, SetStateAction } from 'react';
import { Table, BodyText, Div } from '@gaiads/telia-react-component-library';
import { useTranslation } from 'react-i18next';
import { UserRole } from '@cdaas/api-clients/dist/asset-user-openapi';

import GroupsUsersRow from './GroupsUsersRow';
import { createId } from '../../utils/storeUtils';
import { User } from '../../types';

const userRightsOptions = Object.values(UserRole) as UserRole[];

interface GroupsUsersCardProps {
  users: User[];
  setUsers: Dispatch<SetStateAction<User[]>>;
  allowAddNewUsers?: boolean;
  setUsersAreModified?: Dispatch<SetStateAction<boolean>>;
  hideDeleteIcon?: boolean;
}

const GroupsUsersCard: FunctionComponent<GroupsUsersCardProps> = ({
  users,
  setUsers,
  allowAddNewUsers = false,
  setUsersAreModified,
  hideDeleteIcon = true,
}) => {
  const { t } = useTranslation('globalComponents');
  const tableHeaders = [t('GroupsUsersCard.name'), t('GroupsUsersCard.phoneNumber'), t('GroupsUsersCard.userRights'), ''];

  const handleDropdownInputChange = (userId: string, userRight: UserRole): void => {
    const newUsers = users.map(user => {
      if (user.id === userId) {
        const newUser = { ...user, user_roles: { ...user.user_roles, user: [...user.user_roles.user] } };
        if (newUser.user_roles.user.includes(userRight)) {
          newUser.user_roles.user = newUser.user_roles.user.filter(ur => ur !== userRight);
        } else {
          newUser.user_roles.user = [...newUser.user_roles.user, userRight];
        }
        return newUser;
      } else {
        return user;
      }
    });
    if (setUsersAreModified) setUsersAreModified(true);
    setUsers(newUsers);
  };

  const handleUserDelete = (id: string): void => {
    if (setUsersAreModified) setUsersAreModified(true);
    setUsers(prevUsers => prevUsers.filter(user => user.id !== id));
  };

  const addEmptyUser = () => {
    if (setUsersAreModified) setUsersAreModified(true);
    setUsers([
      ...users,
      {
        name: '',
        newItem: true,
        email: '',
        user_roles: { effective: [], user: [], user_group: [] },
        id: createId(8),
        allowed_procurements: [],
        employee_id: '',
        allow_access_without_bank_id: false,
        prescheduled_onboarding_at: null,
        offboarding_at: null,
        inherited_invoicing_account: {
          name: '',
        },
      },
    ]);
  };

  const handleUserEmailChange = (userId: string, value: string) => {
    if (setUsersAreModified) setUsersAreModified(true);
    setUsers(prevUsers =>
      prevUsers.map(user => {
        if (user.id === userId) {
          return { ...user, email: value };
        } else {
          return user;
        }
      }),
    );
  };

  return (
    <Div padding={{ bottom: 'sm' }}>
      <Table>
        <thead>
          <Table.Row>
            {tableHeaders.map((header, index) => (
              <Table.HeaderCell key={`table-header-${index}`}>
                <BodyText fontWeight="bold">{header}</BodyText>
              </Table.HeaderCell>
            ))}
          </Table.Row>
        </thead>
        <tbody>
          {users.map(user => (
            <GroupsUsersRow
              key={user.id}
              user={user}
              userGroupRightsOptions={userRightsOptions}
              handleDropdownInputChange={handleDropdownInputChange}
              handleDeleteUser={handleUserDelete}
              handleChangeUserEmail={handleUserEmailChange}
              hideDeleteIcon={hideDeleteIcon}
            />
          ))}
        </tbody>
      </Table>
      {allowAddNewUsers && (
        <BodyText clickableAppearance padding={{ size: 'sm' }} color={'purple500'} onClick={addEmptyUser}>
          + {t('GroupsUsersCard.addNewUser')}
        </BodyText>
      )}
    </Div>
  );
};

export default GroupsUsersCard;
